/* eslint-disable consistent-return */
/**
 *
 * Client Exclusive Card
 *
 */

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Card from 'components/Card';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { setLeavingModal } from 'containers/Main/actions';
import { makeSelectProfile } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import {
  getImageFile,
  isIE,
  replacePlaceholders,
  getUserDisplayName,
} from 'utils/stringUtils';
import { getAccentColor } from 'containers/Main/utils';
import CardActionArea from '@material-ui/core/CardActionArea';
import Box from '@material-ui/core/Box';
import { useHistory, useLocation } from 'react-router-dom';
import Mixpanel from 'utils/mixpanelService';
import { getMatchedLabels } from 'components/ResourceItemSmall/utils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import SurveyResultContext from 'components/SurveyResult/SurveyResultContext';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import analyzeClientResource from 'utils/analyzeClientResource';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import HighlightEntry from './HighlightEntry';
import ArrowIcon from './images/ArrowUpRightIcon.svg';
import EmailIcon from './images/EmailIcon.svg';
import PhoneIcon from './images/PhoneIcon.svg';

const useStyles = makeStyles(theme => ({
  imageContainer: {
    display: 'block',
    width: '100%',
    height: 80,
    position: 'relative',
    overflow: 'hidden',
  },
  // darkOverlay: {
  //   position: 'absolute',
  //   top: 0,
  //   left: 0,
  //   width: '100%',
  //   height: '100%',
  //   backgroundColor: 'rgba(0, 0, 0, 0.2)',
  // },
  matchedLabel: {
    zIndex: 2,
    position: 'absolute',
    bottom: 12,
    left: 12,
    padding: '4px 8px',
    borderRadius: 12,
    backgroundColor: '#000000B3',
    color: '#FFFFFF',
    ...theme.typography.pxSmall,
  },
  infoWrapper: {
    padding: '0 20px',
    marginTop: 18,
    marginBottom: 18,
    position: 'relative',
    minHeight: 153,
  },
  title: {
    display: '-webkit-box',
    paddingRight: 14,
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 14,
    lineHeight: '18px',
    maxHeight: 36,
    cursor: 'pointer',
  },
  iconButton: {
    position: 'absolute',
    top: 0,
    right: 12,
    padding: 0,
    color: theme.palette.text.primary,
    fontSize: 20,
  },
  description: {
    display: '-webkit-box',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    fontSize: 12,
    marginTop: 10,
    lineHeight: '18px',
    textOverflow: 'ellipsis',
    maxHeight: 54,
  },
  contactInfoWrapper: {
    backgroundColor: '#F2FAFD',
    padding: '7px 4px',
    marginTop: 8,
    display: 'flex',
    alignItems: 'center',
  },
  contactInfo: {
    fontSize: 12,
    lineHeight: '18px',
    display: '-webkit-box',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  contactIcon: {
    height: 18,
    width: 18,
    marginRight: 6,
  },
  focusHighlight: {
    backgroundColor: 'transparent',
    opacity: 1,
  },
  highlightsContainer: {
    marginTop: 8,
  },
  itemWrapper: {
    position: 'relative',
    height: '100%',
    '&:hover': {
      backgroundColor: ({ isTopicPage }) =>
        !isTopicPage ? '#F8F8F8' : 'inherit',
    },
    '&:hover + #cta-wrapper': {
      backgroundColor: ({ isTopicPage }) =>
        !isTopicPage ? '#F8F8F8' : 'inherit',
    },
  },
  ctaWrapper: {
    left: 14,
    position: 'absolute',
    bottom: -9,
    height: 18,
    backgroundColor: '#FFFFFF',
    padding: '0 6px',
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
    width: 'auto',
    maxWidth: '90%',
    overflowY: 'hidden',
    '&:hover': {
      backgroundColor: ({ isTopicPage }) =>
        !isTopicPage ? '#F8F8F8' : 'inherit',
    },
  },
  cta: {
    display: 'flex',
    alignItems: 'top',
    fontSize: 12,
  },
  ctaIcon: {
    height: 16,
    width: 16,
    marginLeft: 5,
    marginTop: 1,
  },
  cardLink: {
    textDecoration: 'none',
  },
  contentContainer: {
    height: '100%',
  },
}));

function LinkItemNew({ item, page = 'Home', isPdf, grayscale }) {
  const dispatch = useDispatch();
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const locale = useAlgoliaLocale();
  const clientDetails = useSelector(makeSelectClientDetails());
  const profile = useSelector(makeSelectProfile());
  const [showDescription, setShowDescription] = useState(
    _get(clientDetails, 'metadata.linkCardDefaultToExpanded', false),
  );
  const { answersMatchedAudiences, answersMatchedTags } = useContext(
    SurveyResultContext,
  );
  const matchedLabels = getMatchedLabels(
    item,
    answersMatchedAudiences,
    answersMatchedTags,
    locale,
  );
  const accentColor = getAccentColor(clientDetails, '01619B');
  const isTopicPage = page === 'Topic';
  const classes = useStyles({ isTopicPage });
  const location = useLocation();
  const history = useHistory();
  const {
    __typename,
    name,
    title,
    type,
    slug,
    callToAction,
    exitDisclaimer,
    contactInfo,
    contactInfoType,
    contactInfoLink,
  } = item;

  const { isInternal, url } = analyzeClientResource(item);

  const showAsLink = isIE() && !isInternal && url;

  const redirect = () => {
    if (!url && !isInternal) return history.push('/404');

    const entryId = item.objectID || item.sys.id;
    const pageName = location.pathname.split('/')[1];
    const clientName = clientDetails.name;
    const finalPageName = pageName === '/' ? 'homePage' : `${pageName}Page`;

    if (isInternal) {
      Mixpanel.track(`Client Resource Card Clicked - ${page}`, {
        article: name || title,
        page: finalPageName,
        slug,
        resource: url,
        client: clientName,
        entryId,
      });

      history.push(url);
    }
    if (showAsLink) return false;
    if (!isInternal) {
      Mixpanel.track(`Client Resource Card Exited - ${page}`, {
        page: location.pathname,
        exitPage: url,
        client: clientName,
        entryId,
      });

      const finalUrl = url.includes('http') ? url : `https://${url}`;
      if (!finalUrl.includes('assets.ctfassets.net')) {
        dispatch(
          setLeavingModal({
            visible: true,
            title:
              getAlgoliaLocalizedField(item, 'title', locale) ||
              getAlgoliaLocalizedField(item, 'name', locale),
            url: finalUrl,
            organization: clientDetails.name,
            resourceType: type || null,
            disclaimer: exitDisclaimer,
          }),
        );
      } else {
        window.open(finalUrl, '_blank', 'noopener');
      }
    }
  };

  const file = getImageFile(item);
  const ctaText =
    !url &&
    !callToAction &&
    (__typename === 'Source' ||
      _get(item, 'sys.contentType.sys.id') === 'source')
      ? _get(siteCopy, 'pageCopy.clientExclusiveWaitLinkLabel')
      : callToAction ||
        _get(siteCopy, 'pageCopy.clientExclusiveLearnMoreLabel');

  const cardRootStyle = file
    ? {
        backgroundImage: `url(${file})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }
    : {};
  if (file && grayscale) {
    cardRootStyle.filter = 'grayscale(1)';
  }
  const cardRootColor = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: `#${accentColor}`,
    opacity: 0.8,
    mixBlendMode: 'screen',
  };

  const toggleDescriptionVisibility = e => {
    e.stopPropagation();
    setShowDescription(!showDescription);
  };

  const onCTAFocus = () => {
    if (!isTopicPage) {
      document.getElementById(`item-wrapper-${slug}`).style.backgroundColor =
        '#F8F8F8';
    }
  };
  const onCTABlur = () => {
    if (!isTopicPage) {
      document
        .getElementById(`item-wrapper-${slug}`)
        .style.removeProperty('background-color');
    }
  };

  const cardTitle =
    getAlgoliaLocalizedField(item, 'title', locale) ||
    getAlgoliaLocalizedField(item, 'name', locale);
  const cardSubtitle =
    getAlgoliaLocalizedField(item, 'subtitle', locale) ||
    getAlgoliaLocalizedField(item, 'shortDescription', locale);
  const hasDescription = !!cardSubtitle;
  const hasContactInfo = !!contactInfo;
  const hasHighlights =
    getAlgoliaLocalizedField(item, 'highlights', locale, []).length > 0;
  const ContactInfoIcon =
    contactInfoType === 'Phone'
      ? PhoneIcon
      : contactInfoType === 'Email'
      ? EmailIcon
      : null;
  const contactInfoUrl = replacePlaceholders(
    contactInfoType === 'Phone'
      ? `tel:${contactInfoLink || contactInfo}`
      : contactInfoType === 'Email'
      ? `mailto:${contactInfoLink || contactInfo}`
      : '',
    !_isEmpty(profile)
      ? {
          '<<YOUR NAME>>': getUserDisplayName(profile),
        }
      : {},
  );

  const content = () => (
    <Card
      background="#FFFFFF"
      padding={0}
      borderRadius={8}
      borderColor="#E9ECED"
      mb={isTopicPage ? 20 : 0}
      width={isTopicPage ? '100%' : ''}
      className={classes.itemRoot}
      link
    >
      <CardActionArea
        onClick={() => redirect(item)}
        id={`item-wrapper-${slug}`}
        classes={{
          root: classes.itemWrapper,
          focusHighlight: classes.focusHighlight,
        }}
        disableRipple
      >
        <Grid container className={classes.contentContainer}>
          <Grid item xs={12} className={classes.imageContainer}>
            {file && <div style={cardRootStyle} />}
            <div style={cardRootColor} />
            {/* <div className={classes.darkOverlay} /> */}
            {!_isEmpty(matchedLabels) && (
              <Typography component="span" className={classes.matchedLabel}>
                {matchedLabels[0]}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} className={classes.infoWrapper}>
            <Typography
              variant="h6"
              color="textPrimary"
              align="left"
              className={classes.title}
              name="title"
            >
              {cardTitle}
            </Typography>
            {hasDescription && (showDescription || !hasHighlights) && (
              <Typography
                color="textPrimary"
                align="left"
                className={classes.description}
                name="description"
              >
                {cardSubtitle}
              </Typography>
            )}
            {((hasDescription && hasHighlights) || hasContactInfo) && (
              <IconButton
                className={classes.iconButton}
                component="span"
                disableRipple
                disableFocusRipple
                onClick={toggleDescriptionVisibility}
              >
                {!showDescription ? (
                  <KeyboardArrowDown fontSize="inherit" />
                ) : (
                  <KeyboardArrowUp fontSize="inherit" />
                )}
              </IconButton>
            )}
            {hasHighlights && (
              <Box className={classes.highlightsContainer}>
                {getAlgoliaLocalizedField(item, 'highlights', locale, [])
                  .slice(0, 3)
                  .map(highlight => (
                    <HighlightEntry value={highlight} link key={highlight} />
                  ))}
              </Box>
            )}
            {showDescription && hasContactInfo && (
              <a href={contactInfoUrl} onClick={e => e.stopPropagation()}>
                <Box className={classes.contactInfoWrapper}>
                  {ContactInfoIcon && (
                    <span>
                      <img
                        className={classes.contactIcon}
                        src={ContactInfoIcon}
                        alt="Call"
                      />
                    </span>
                  )}
                  <Typography
                    color="textPrimary"
                    align="left"
                    className={classes.contactInfo}
                    name="contact-info"
                  >
                    {contactInfo}
                  </Typography>
                </Box>
              </a>
            )}
          </Grid>
        </Grid>
      </CardActionArea>
      <CardActionArea
        id="cta-wrapper"
        onClick={() => redirect(item)}
        classes={{
          root: classes.ctaWrapper,
          focusHighlight: classes.focusHighlight,
        }}
        onMouseOver={onCTAFocus}
        onFocus={onCTAFocus}
        onMouseOut={onCTABlur}
        onBlur={onCTABlur}
        disableRipple
      >
        <Typography align="left" color="textSecondary" className={classes.cta}>
          {ctaText}
          <img
            src={ArrowIcon}
            className={classes.ctaIcon}
            alt="Call to action"
          />
        </Typography>
      </CardActionArea>
    </Card>
  );
  if (showAsLink || isPdf)
    return (
      <a href={url} target="_blank" className={classes.cardLink}>
        {content()}
      </a>
    );
  return content();
}

LinkItemNew.propTypes = {
  item: PropTypes.object,
  clientDetails: PropTypes.object,
  page: PropTypes.string,
  isBiggerCard: PropTypes.bool,
  isPdf: PropTypes.bool,
  grayscale: PropTypes.bool,
};

export default React.memo(LinkItemNew);
